import * as React from 'react';
import { useEffect, useRef } from 'react';
import { mergeRight } from 'ramda';
import { assert } from '../lib/utils';

export default function({ style, type, width, height, onInit }: {
    style?: React.CSSProperties,
    type: 'pen' | 'eraser' | null,
    width: number,
    height: number,
    onInit?: (controller: controller) => void,
}) {
    const state = useRef<{
        pos: position,
        viewport: DOMRect,
        scaleW: number,
        scaleH: number,
    } | null>(null);
    const canvas = useRef<HTMLCanvasElement>(null);
    const eraserW = 50;

    useEffect(() => {
        if (onInit !== undefined) {
            assert(canvas.current !== null, '');
            onInit({
                export: () => canvas.current!.toDataURL(),
                import: (datauri) => {
                    const img = new Image();
                    img.addEventListener('load', () => {
                        canvas.current!.getContext('2d')!.drawImage(img, 0, 0);
                    });
                    img.src = datauri;
                }
            });
        }
    }, []);

    return (
    <canvas ref={canvas}
        width={width}
        height={height}
        style={mergeRight({
            pointerEvents: type !== null ? 'auto' : 'none',
        } as const, style || {})}
        onMouseDown={e => {
            assert(canvas.current !== null, '');
            const viewport = canvas.current.getBoundingClientRect();
            const scaleW = viewport.width / width;
            const scaleH = viewport.height / height;
            if (type === 'pen') {
                const x = (e.pageX - canvas.current.offsetLeft) / scaleW - (viewport.left / scaleW);
                const y = (e.pageY - canvas.current.offsetTop) / scaleH - (viewport.top / scaleH);
                state.current = {
                    pos: { x, y },
                    viewport, scaleW, scaleH
                };
            } else {
                assert(canvas.current !== null, '');
                const ctx = canvas.current.getContext('2d');
                assert(ctx !== null, 'canvas context is null');
                const x = ((e.pageX - canvas.current.offsetLeft) / scaleW - (viewport.left / scaleW)) - (eraserW / 2);
                const y = ((e.pageY - canvas.current.offsetTop) / scaleH - (viewport.top / scaleH)) - (eraserW / 2);
                ctx.clearRect(x, y, eraserW, eraserW);
                state.current = {
                    pos: { x, y },
                    viewport, scaleW, scaleH
                };
            }
        }}
        onMouseUp={e => state.current = null}
        onMouseMove={e => {
            if (state.current) {
                assert(canvas.current !== null, '');
                const ctx = canvas.current.getContext('2d');
                assert(ctx !== null, 'canvas context is null');
                const { viewport, scaleW, scaleH } = state.current;
                if (type === 'pen') {
                    const x = (e.pageX - canvas.current.offsetLeft) / scaleW - (viewport.left / scaleW);
                    const y = (e.pageY - canvas.current.offsetTop) / scaleH - (viewport.top / scaleH);
                    ctx.lineWidth = 55;
                    ctx.beginPath();
                    ctx.moveTo(state.current.pos.x, state.current.pos.y);
                    ctx.lineTo(x, y);
                    ctx.closePath();
                    ctx.stroke();
                    state.current.pos = { x, y };
                } else {
                    const x = ((e.pageX - canvas.current.offsetLeft) / scaleW - (viewport.left / scaleW)) - (eraserW / 2);
                    const y = ((e.pageY - canvas.current.offsetTop) / scaleH - (viewport.top / scaleH)) - (eraserW / 2);
                    ctx.clearRect(x, y, eraserW, eraserW);
                }
            }
        }}
    />);
}

export type controller = {
    export: () => string,
    import: (datauri: string) => void,
}

type position = {
    x: number,
    y: number,
}
